"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FaCheck } from "react-icons/fa";
import { HeroCtaButton, DEFAULT_CALENDLY_URL } from "@/components/ui/hero-cta-button";
import { OFFERS_CARD_ENTRANCE_DELAY } from "./offers-hero-motion";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;

function PricingCardEntrance({
  children,
  direction,
  delay,
}: {
  children: ReactNode;
  direction: "left" | "right";
  delay: number;
}) {
  const reducedMotion = useReducedMotion();
  const spreadX = direction === "left" ? 72 : -72;

  return (
    <motion.div
      className="h-full"
      initial={
        reducedMotion
          ? false
          : {
              opacity: 0,
              x: spreadX,
              y: 36,
              scale: 0.9,
              rotateY: direction === "left" ? 14 : -14,
              filter: "blur(10px)",
            }
      }
      animate={{
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        rotateY: 0,
        filter: "blur(0px)",
      }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : {
              delay,
              duration: 0.82,
              ease: SEQUENCE_EASE,
            }
      }
      style={{ transformStyle: "preserve-3d" }}
    >
      {children}
    </motion.div>
  );
}

const WEB_ESSENTIEL_ITEMS = [
  "Site vitrine sur-mesure, design + développement",
  "Livraison en 3-4 semaines",
  "Support par email",
  "Corrections mineures pendant 2 semaines après livraison",
] as const;

const WEB_EVOLUTION_ITEMS = [
  "Site sur-mesure, design + développement",
  "Livraison en 2 semaines",
  "SEO + stratégie intégrés dès le départ",
  "Support 7j/7",
  "Suivi et ajustements inclus jusqu'à vos premiers résultats",
] as const;

function PremiumCheckList({
  items,
  variant,
  label,
}: {
  items: readonly string[];
  variant: "light" | "dark";
  label: string;
}) {
  const isDark = variant === "dark";

  return (
    <div className="space-y-2.5 md:space-y-3">
      <p
        className={`text-xs font-semibold md:text-[0.8125rem] ${
          isDark ? "text-white/90" : "text-[#1C1C1C]"
        }`}
      >
        {label}
      </p>
      <ul className="space-y-2.5 md:space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 md:gap-2.5">
            <span
              className={`mt-0.5 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border md:h-5 md:w-5 ${
                isDark
                  ? "border-white/25 bg-white/10 text-white/90"
                  : "border-zinc-300 bg-zinc-50 text-zinc-500"
              }`}
            >
              <FaCheck className="text-[8px] md:text-[9px]" aria-hidden="true" />
            </span>
            <span
              className={`text-xs leading-snug md:text-[0.8125rem] md:leading-relaxed ${
                isDark ? "text-white/75" : "text-zinc-600"
              }`}
            >
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PriceBlock({
  amount,
  subline,
  variant,
}: {
  amount: string;
  subline: string;
  variant: "light" | "dark";
}) {
  const isDark = variant === "dark";

  return (
    <div className="space-y-2.5">
      <p
        className={`text-[2.15rem] font-bold leading-none tracking-[-0.04em] md:text-[2.35rem] lg:text-[2.5rem] ${
          isDark ? "text-white" : "text-[#1C1C1C]"
        }`}
      >
        {amount}
      </p>
      <div
        className={`border-t border-dashed ${
          isDark ? "border-white/15" : "border-zinc-200"
        }`}
      />
      <p
        className={`text-xs font-medium leading-snug md:text-[0.8125rem] ${
          isDark ? "text-white/60" : "text-zinc-500"
        }`}
      >
        {subline}
      </p>
    </div>
  );
}

function WebEssentielCard() {
  return (
    <article className="group relative grid h-full min-h-[28.5rem] grid-rows-[auto_auto_auto_minmax(0,1fr)] overflow-hidden rounded-[1.35rem] border border-zinc-200/90 bg-white p-5 shadow-[0_20px_50px_-16px_rgba(15,23,42,0.12),0_6px_20px_-8px_rgba(15,23,42,0.07)] md:min-h-[29rem] md:rounded-[1.5rem] md:p-6 [backface-visibility:hidden]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
      >
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-zinc-50/80 to-transparent" />
      </div>

      <div className="relative mb-4 flex items-start justify-between gap-2 md:mb-4">
        <div className="space-y-0.5">
          <h2 className="text-lg font-bold tracking-tight text-[#1C1C1C] md:text-xl">
            WebEssentiel
          </h2>
          <p className="text-xs font-medium leading-snug text-zinc-500 md:text-[0.8125rem]">
            Pour un site pro qui évolue à votre rythme.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-zinc-600">
          Entrée de gamme
        </span>
      </div>

      <div className="relative mb-4 md:mb-5">
        <PriceBlock
          amount="3 000 €"
          subline="Idéal 1er site · livraison 3-4 sem."
          variant="light"
        />
      </div>

      <div className="relative mb-5 min-h-[2.75rem] md:mb-5">
        <Link
          href={DEFAULT_CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-full min-h-[2.75rem] w-full items-center justify-center rounded-full border border-zinc-200 bg-gradient-to-b from-white to-zinc-50 px-5 py-3 text-sm font-semibold text-[#1C1C1C] shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18),inset_0_1px_0_rgba(255,255,255,1)] transition hover:border-zinc-300 hover:from-zinc-50 hover:to-zinc-100"
        >
          Choisir WebEssentiel
        </Link>
      </div>

      <div>
        <PremiumCheckList
          items={WEB_ESSENTIEL_ITEMS}
          variant="light"
          label="La formule WebEssentiel inclut :"
        />
      </div>
    </article>
  );
}

function WebEvolutionCard() {
  return (
    <article className="group relative grid h-full min-h-[28.5rem] grid-rows-[auto_auto_auto_minmax(0,1fr)] overflow-hidden rounded-[1.35rem] border border-zinc-800/80 bg-[#141414] p-5 shadow-[0_20px_50px_-16px_rgba(0,0,0,0.5),0_6px_20px_-8px_rgba(0,0,0,0.22)] md:min-h-[29rem] md:rounded-[1.5rem] md:p-6 [backface-visibility:hidden]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
      >
        <div className="absolute inset-x-0 top-0 h-28 bg-[radial-gradient(ellipse_80%_70%_at_50%_0%,rgba(16,185,129,0.22),transparent_70%)]" />
      </div>

      <div className="relative mb-4 flex items-start justify-between gap-2 md:mb-4">
        <div className="space-y-0.5">
          <h2 className="text-lg font-bold tracking-tight text-white md:text-xl">
            WebEvolution
          </h2>
          <p className="text-xs font-medium leading-snug text-white/65 md:text-[0.8125rem]">
            Pour un site qui travaille vraiment pour vous.
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide text-zinc-600">
          Meilleur ROI
        </span>
      </div>

      <div className="relative mb-4 md:mb-5">
        <PriceBlock
          amount="7 500 €"
          subline="Accompagnement complet · livraison 2 sem."
          variant="dark"
        />
      </div>

      <div className="relative mb-5 min-h-[2.75rem] w-full md:mb-5 [&>a]:h-full [&>a]:min-h-[2.75rem] [&>a]:w-full [&>a]:justify-center">
        <HeroCtaButton variant="light" size="compact" href={DEFAULT_CALENDLY_URL}>
          Réserver mon audit gratuit
        </HeroCtaButton>
      </div>

      <div>
        <PremiumCheckList
          items={WEB_EVOLUTION_ITEMS}
          variant="dark"
          label="La formule WebEvolution inclut :"
        />
      </div>
    </article>
  );
}

function PricingCardShell({
  children,
  badge,
}: {
  children: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <div className="relative h-full overflow-visible px-1 pb-2 pt-5 md:px-1.5 md:pt-5 md:pb-3">
      {badge}
      {children}
    </div>
  );
}

function RecommendedBadge({ delay }: { delay: number }) {
  const reducedMotion = useReducedMotion();

  return (
    <motion.span
      className="absolute left-1/2 top-5 z-20 inline-flex -translate-x-1/2 -translate-y-1/2 items-center whitespace-nowrap rounded-full border border-emerald-400/30 bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 px-3 py-0.5 text-[0.62rem] font-bold uppercase tracking-widest text-white shadow-[0_8px_24px_rgba(5,150,105,0.45)]"
      initial={reducedMotion ? false : { opacity: 0, scale: 0.72, y: -6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { delay, duration: 0.5, ease: SEQUENCE_EASE }
      }
    >
      Recommandé
    </motion.span>
  );
}

export function OffersPricingCards() {
  const cardDelay = OFFERS_CARD_ENTRANCE_DELAY;
  const badgeDelay = cardDelay + 0.42;

  return (
    <div className="relative -mt-2 overflow-visible bg-[#f3f3f3] px-4 pb-10 md:-mt-4 md:px-6 md:pb-12">
      <div
        className="relative mx-auto grid max-w-[60rem] gap-6 overflow-visible md:grid-cols-2 md:items-stretch md:gap-5 lg:gap-6"
        style={{
          perspective: "1400px",
          perspectiveOrigin: "50% 50%",
        }}
      >
        <div
          className="order-2 h-full overflow-visible [backface-visibility:hidden] transition-transform duration-500 ease-out md:order-1 md:[transform:rotateY(-11deg)_translateZ(0)] md:hover:[transform:rotateY(-7deg)_translateZ(0)]"
          style={{ transformStyle: "preserve-3d" }}
        >
          <PricingCardEntrance direction="left" delay={cardDelay}>
            <PricingCardShell>
              <WebEssentielCard />
            </PricingCardShell>
          </PricingCardEntrance>
        </div>
        <div
          className="order-1 h-full overflow-visible [backface-visibility:hidden] transition-transform duration-500 ease-out md:order-2 md:[transform:rotateY(11deg)_translateZ(0)] md:hover:[transform:rotateY(7deg)_translateZ(0)]"
          style={{ transformStyle: "preserve-3d" }}
        >
          <PricingCardEntrance direction="right" delay={cardDelay}>
            <PricingCardShell badge={<RecommendedBadge delay={badgeDelay} />}>
              <WebEvolutionCard />
            </PricingCardShell>
          </PricingCardEntrance>
        </div>
      </div>
    </div>
  );
}
